import React, { useEffect, useRef } from "react";
import { gsap } from "gsap/dist/gsap";
import { useLanguage } from "@/contexts/LanguageContext";

interface CalendlyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CalendlyModal({ isOpen, onClose }: CalendlyModalProps) {
  const { t } = useLanguage();
  const modalRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";

      // Animation d'ouverture
      gsap.set([overlayRef.current, contentRef.current], {
        opacity: 0,
      });

      gsap.set(contentRef.current, {
        scale: 0.9,
        y: 50,
      });

      const tl = gsap.timeline();
      tl.to(overlayRef.current, {
        opacity: 1,
        duration: 0.3,
        ease: "power2.out",
      }).to(
        contentRef.current,
        {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 0.4,
          ease: "back.out(1.7)",
        },
        "-=0.1"
      );

      // No need for complex script management - iframe approach is more reliable
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleClose = () => {
    // Animation de fermeture
    const tl = gsap.timeline({
      onComplete: onClose,
    });

    tl.to(contentRef.current, {
      opacity: 0,
      scale: 0.9,
      y: 50,
      duration: 0.3,
      ease: "power2.in",
    }).to(
      overlayRef.current,
      {
        opacity: 0,
        duration: 0.2,
        ease: "power2.in",
      },
      "-=0.1"
    );
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
    >
      {/* Overlay */}
      <div
        ref={overlayRef}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleOverlayClick}
      />

      {/* Modal Content */}
      <div
        ref={contentRef}
        className="relative bg-white rounded-3xl shadow-2xl shadow-black/20 w-full max-w-4xl max-h-[90vh] overflow-hidden border border-gray-100 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white flex-shrink-0">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">
              {t("calendlyModal.title")}
            </h2>
            <p className="text-gray-600 mt-1">
              {t("calendlyModal.description")}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200 group"
            aria-label={t("calendlyModal.closeModal")}
          >
            <svg
              className="w-6 h-6 text-gray-500 group-hover:text-gray-700"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Calendly Widget Container - Scrollable */}
        <div className="bg-white flex-1 overflow-auto">
          <div className="bg-white overflow-hidden">
            {/* Calendly iframe - more reliable than inline widget */}
            <iframe
              src={`https://calendly.com/contact-featcal/30min?embed_domain=${
                typeof window !== "undefined"
                  ? window.location.hostname
                  : "featcal.com"
              }&embed_type=Inline`}
              width="100%"
              height="700"
              frameBorder="0"
              title="Calendly Scheduling"
              style={{ minWidth: "320px" }}
            />
          </div>
        </div>

        {/* Footer - Always visible */}
        <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-white border-t border-gray-100 flex-shrink-0">
          <p className="text-sm text-gray-600 text-center">
            {t("calendlyModal.contactMessage")}{" "}
            <a
              href="mailto:contact@featcal.com"
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              contact@featcal.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
